import { forwardRef, useImperativeHandle, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Box } from '@mui/material';

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  toBlob: () => Promise<Blob | null>;
}

interface SignaturePadProps {
  width?: number;
  height?: number;
}

/** Digi Pad (Phase 78) — a thin wrapper around react-signature-canvas
 * (itself a wrapper around signature_pad), chosen specifically because it
 * already handles mouse/touch/pointer events correctly across desktop and
 * mobile without hand-rolled event plumbing. A fixed canvas pixel size
 * (not CSS-percentage-scaled) avoids the coordinate-mapping mismatch that
 * comes from drawing-surface size differing from the canvas's own
 * width/height attributes. */
export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  ({ width = 340, height = 120 }, ref) => {
    const padRef = useRef<SignatureCanvas>(null);

    useImperativeHandle(ref, () => ({
      clear: () => padRef.current?.clear(),
      isEmpty: () => padRef.current?.isEmpty() ?? true,
      toBlob: () =>
        new Promise((resolve) => {
          if (!padRef.current) {
            resolve(null);
            return;
          }
          // getTrimmedCanvas() (crops away the surrounding whitespace) can
          // throw in some browser/canvas-size combinations — falls back to
          // the plain, untrimmed canvas rather than failing the save
          // outright over a cosmetic trim.
          let canvas: HTMLCanvasElement;
          try {
            canvas = padRef.current.getTrimmedCanvas();
          } catch {
            canvas = padRef.current.getCanvas();
          }
          canvas.toBlob((blob) => resolve(blob), 'image/png');
        }),
    }));

    return (
      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'inline-block',
          touchAction: 'none',
        }}
      >
        <SignatureCanvas
          ref={padRef}
          penColor="black"
          canvasProps={{ width, height, style: { touchAction: 'none' } }}
        />
      </Box>
    );
  },
);
SignaturePad.displayName = 'SignaturePad';
